/**
 * Custom hook for tutors
 */

import { useState, useEffect } from 'react';
import tutorService from '../services/tutorService';
import { toast } from 'react-hot-toast';

/**
 * Custom hook to manage tutors
 * 
 * @returns {Object} Tutor methods and state
 */
const useTutors = () => {
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [universities, setUniversities] = useState([]);
  const [courseCodes, setCourseCodes] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Load tutors with filters
  const loadTutors = async (filters = {}, reset = true) => {
    try {
      setLoading(true);
      
      const docToUse = reset ? null : lastDoc;
      const result = await tutorService.getApprovedTutors(filters, 12, docToUse);
      
      if (reset) {
        setTutors(result.tutors);
      } else {
        setTutors(prev => [...prev, ...result.tutors]);
      }
      
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Error loading tutors:', error);
      toast.error('Failed to load tutors');
    } finally {
      setLoading(false);
    }
  };

  // Load more tutors for pagination
  const loadMoreTutors = async (filters = {}) => {
    if (!hasMore || loading) return;
    
    await loadTutors(filters, false);
  };

  // Search tutors by course code
  const searchTutorsByCourse = async (courseCode) => {
    if (!courseCode.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const results = await tutorService.searchTutorsByCourse(courseCode);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching tutors:', error);
      toast.error('Failed to search tutors');
    } finally {
      setSearching(false);
    }
  };

  // Clear search results
  const clearSearch = () => {
    setSearchResults([]);
  };

  // Load universities
  const loadUniversities = async () => {
    try {
      const universityList = await tutorService.getUniversities();
      setUniversities(universityList);
    } catch (error) {
      console.error('Error loading universities:', error);
    }
  };

  // Load course codes
  const loadCourseCodes = async () => {
    try {
      const codes = await tutorService.getCourseCodes();
      setCourseCodes(codes);
    } catch (error) {
      console.error('Error loading course codes:', error);
    }
  };

  // Get tutor by ID
  const getTutorById = async (tutorId) => {
    try {
      return await tutorService.getTutorById(tutorId);
    } catch (error) {
      console.error('Error getting tutor by ID:', error);
      toast.error('Failed to load tutor details');
      return null;
    }
  };

  // Load tutors by university
  const loadTutorsByUniversity = async (university) => {
    try {
      setLoading(true);
      const results = await tutorService.getTutorsByUniversity(university);
      setTutors(results);
      setHasMore(false); // University-specific searches don't support pagination yet
    } catch (error) {
      console.error('Error loading tutors by university:', error);
      toast.error('Failed to load tutors');
    } finally {
      setLoading(false);
    }
  };

  // Initialize data on mount
  useEffect(() => {
    loadTutors();
    loadUniversities();
    loadCourseCodes();
  }, []);

  return {
    tutors,
    loading,
    universities,
    courseCodes,
    searchResults,
    searching,
    hasMore,
    loadTutors,
    loadMoreTutors,
    searchTutorsByCourse,
    clearSearch,
    getTutorById,
    loadTutorsByUniversity
  };
};

export default useTutors;